import type { PatternStatsResponse } from "./patterns.api";

export type SummaryTile = {
  id: string;
  label: string;
  value: string;
  note: string;
  /** Where the tile goes when it cannot open a board in the explorer. */
  to: string | null;
  /** Explorer item the tile opens, when that item has a saved position. */
  itemId: string | null;
};

/** Clock error rates below this many measured moves are too thin to show. */
const MIN_CLOCK_MOVES = 10;
const PHASES = [
  { key: "opening", range: "MOVES 1–10" },
  { key: "middlegame", range: "MOVES 11–30" },
  { key: "late", range: "MOVES 31+" },
] as const;

/** One line per measured habit; thin or empty measurements are withheld rather than shown as zeros. */
export const buildSummaryTiles = (
  stats: Pick<
    PatternStatsResponse,
    | "analyzedGameCount"
    | "cleanGameCount"
    | "cleanGameExample"
    | "stageErrorCounts"
    | "clockHabits"
    | "recentVsPriorTrend"
    | "tacticalOpportunityCounts"
    | "ignoredThreats"
    | "opponentBlunderCount"
    | "missedOpportunityExample"
    | "steadyReplyCount"
    | "steadyReplyExample"
  > &
    Partial<Pick<PatternStatsResponse, "tacticalVerification">>,
  /** Explorer items that exist, so a tile opens its board only when there is one. */
  itemIds: ReadonlySet<string> = new Set(),
): SummaryTile[] => {
  const tiles: SummaryTile[] = [];
  const item = (id: string) => (itemIds.has(id) ? id : null);
  const counts = stats.stageErrorCounts;
  const total = counts.opening + counts.middlegame + counts.late;
  if (total > 0) {
    const heaviest = PHASES.reduce((best, phase) => (counts[phase.key] > counts[best.key] ? phase : best));
    tiles.push({
      id: "phase",
      label: "WHERE ERRORS HAPPEN",
      value: heaviest.range,
      note: `${counts[heaviest.key]} of ${total} costly moves.`,
      to: "/trends",
      itemId: item(`insight:phase_${heaviest.key}`),
    });
  }
  const clock = stats.clockHabits;
  if (clock.rushedMoves >= MIN_CLOCK_MOVES && clock.rushedErrorRate != null)
    tiles.push({
      id: "fast",
      label: `FAST MOVES (≤${clock.rushedSeconds}S)`,
      value: `${Math.round(clock.rushedErrorRate)}% errors`,
      note: `${clock.rushedMoves} moves played quickly.`,
      to: "/trends",
      itemId: item("insight:fast"),
    });
  const trend = stats.recentVsPriorTrend;
  if (trend.errorMovesPerGameDelta != null && trend.recent && trend.prior)
    tiles.push({
      id: "form",
      label: "RECENT FORM",
      value: `${trend.prior.errorMovesPerGame} → ${trend.recent.errorMovesPerGame}`,
      note: `Costly moves per game, previous vs latest ${trend.cohortSize}.`,
      to: "/trends",
      itemId: null,
    });
  if (stats.opponentBlunderCount > 0) {
    tiles.push({
      id: "missed",
      label: "MISSED CHANCES",
      value: `${stats.ignoredThreats} of ${stats.opponentBlunderCount}`,
      note: "Opponent blunders followed by your costly move.",
      to: stats.missedOpportunityExample ? `/games/${stats.missedOpportunityExample.gameId}` : null,
      itemId: item("moment:missed_opportunity"),
    });
    tiles.push({
      id: "steady",
      label: "STEADY REPLIES",
      value: `${stats.steadyReplyCount} of ${stats.opponentBlunderCount}`,
      note: "Opponent blunders you answered without a costly move.",
      to: stats.steadyReplyExample ? `/games/${stats.steadyReplyExample.gameId}` : null,
      itemId: item("moment:steady_reply"),
    });
  }
  if (stats.analyzedGameCount > 0)
    tiles.push({
      id: "clean",
      label: "CLEAN GAMES",
      value: `${stats.cleanGameCount} of ${stats.analyzedGameCount}`,
      note: "Analyzed games with no mistakes or blunders.",
      to: stats.cleanGameExample ? `/games/${stats.cleanGameExample.gameId}` : null,
      itemId: null,
    });
  const { missedChecks, missedCaptures } = stats.tacticalOpportunityCounts;
  // Missed checks and captures are only counted in verified games; with none verified the count means nothing yet.
  const verification = stats.tacticalVerification;
  if (verification?.verifiedGames === 0 && stats.analyzedGameCount > 0) return tiles;
  tiles.push({
    id: "forcing",
    label: `CHECKS & CAPTURES PASSED${(verification?.needsReanalysisGames ?? 0) > 0 ? " SO FAR" : ""}`,
    value: String(missedChecks + missedCaptures),
    note: `${missedChecks} checks · ${missedCaptures} captures, each verified.`,
    to: null,
    itemId: null,
  });
  return tiles;
};
